import { Field as BaseField } from "@base-ui/react/field";
import { NumberField as BaseNumberField } from "@base-ui/react/number-field";
import clsx from "clsx";
import { useMemo, type ReactNode, type Ref } from "react";
import { Decimal, numberToDecimalString, type DecimalString } from "@/lib/decimal";
import { FieldFooter, FieldLabel, type FieldLabelProps } from "../Field/FieldParts";
import { Icon } from "../Icon/Icon";
import styles from "../Field/Field.module.css";

export interface NumberFieldProps extends FieldLabelProps {
  /** Decimal string from the API (or null for empty). Never a float. */
  value?: DecimalString | null;
  defaultValue?: DecimalString | null;
  /** Receives a clean decimal string ("0.12", never "0.12000000000000001") or null when cleared. */
  onValueChange?: (value: DecimalString | null) => void;
  min?: number;
  max?: number;
  /** Step at the balance resolution for masses (0.001 g). Also sets the default decimals. */
  step?: number;
  /** Maximum decimal places accepted and displayed; defaults to the step's decimals (or 8). */
  decimals?: number;
  /** Unit suffix inside the field ("g", "%", "mm"). */
  unit?: ReactNode;
  /** Show −/+ buttons (off by default; masses are typed or read from the balance). */
  steppers?: boolean;
  size?: "sm" | "md" | "lg";
  name?: string;
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  /** Fires when the user commits a value (blur or Enter), with the clean decimal string. */
  onValueCommitted?: (value: DecimalString | null) => void;
  className?: string;
  inputRef?: Ref<HTMLInputElement>;
}

function decimalsOf(step: number | undefined): number {
  if (step === undefined) return 8;
  const text = String(step);
  const exponent = /e-(\d+)$/.exec(text);
  if (exponent) return Number(exponent[1]);
  return text.includes(".") ? (text.split(".")[1]?.length ?? 0) : 0;
}

function toNumber(value: DecimalString | null | undefined): number | null | undefined {
  if (value === undefined) return undefined;
  const decimal = Decimal.tryFrom(value);
  return decimal === null ? null : decimal.toNumber();
}

/**
 * A labelled number input (Base UI NumberField) with a decimal-string API: values come from and
 * go to the backend as strings, so no float drift reaches the server. Accepts "," or "." per
 * locale, inputMode="decimal", and no scrub area.
 */
export function NumberField({
  label,
  hideLabel,
  description,
  error,
  optional,
  value,
  defaultValue,
  onValueChange,
  onValueCommitted,
  min,
  max,
  step,
  decimals,
  unit,
  steppers = false,
  size = "md",
  name,
  id,
  placeholder,
  disabled,
  readOnly,
  required,
  className,
  inputRef,
}: NumberFieldProps) {
  const maxDecimals = decimals ?? decimalsOf(step);
  // With a step (0.001 g), values show at that precision: "0.120", like the balance does.
  const minDecimals = step !== undefined ? Math.min(decimalsOf(step), maxDecimals) : 0;
  const format = useMemo<Intl.NumberFormatOptions>(
    () => ({ minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals, useGrouping: false }),
    [minDecimals, maxDecimals],
  );
  const toDecimal = (next: number | null) => (next === null ? null : numberToDecimalString(next, maxDecimals));

  return (
    <BaseField.Root
      name={name}
      disabled={disabled}
      invalid={error ? true : undefined}
      className={clsx(styles.field, className)}
    >
      <BaseNumberField.Root
        id={id}
        name={name}
        value={toNumber(value)}
        defaultValue={toNumber(defaultValue) ?? undefined}
        onValueChange={onValueChange ? (next) => onValueChange(toDecimal(next)) : undefined}
        onValueCommitted={onValueCommitted ? (next) => onValueCommitted(toDecimal(next)) : undefined}
        min={min}
        max={max}
        step={step ?? "any"}
        format={format}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        inputRef={inputRef}
        className={styles.contents}
      >
        <FieldLabel label={label} hideLabel={hideLabel} optional={optional} />
        <BaseNumberField.Group className={styles.control} data-size={size}>
          {steppers ? (
            <BaseNumberField.Decrement className={styles.stepper} aria-label="Decrease">
              <Icon name="minus" />
            </BaseNumberField.Decrement>
          ) : null}
          <BaseNumberField.Input
            className={styles.input}
            data-numeric=""
            inputMode="decimal"
            placeholder={placeholder}
          />
          {unit ? <span className={styles.adornment}>{unit}</span> : null}
          {steppers ? (
            <BaseNumberField.Increment className={styles.stepper} aria-label="Increase">
              <Icon name="plus" />
            </BaseNumberField.Increment>
          ) : null}
        </BaseNumberField.Group>
      </BaseNumberField.Root>
      <FieldFooter description={description} error={error} />
    </BaseField.Root>
  );
}
